<template>
  <v-container py-0>
    <v-progress-linear
      :active="loading"
      height="3"
      :indeterminate="true"
      class="my-1"
    />
    <v-card color="transparent" flat light>
      <v-card-text class="py-0">
        <v-layout wrap row>
          <v-flex md4 xs12 px-2>
            <v-text-field
              v-model="entity.name"
              v-validate="'required'"
              :label="$t('fields.global.name') + ' (*)'"
              :data-vv-name="$t('fields.global.name')"
              :error-messages="errors.collect($t('fields.global.name'))"
            />
          </v-flex>
          <v-flex md4 xs12 px-2>
            <v-text-field
              v-model="entity.abbreviation"
              :label="$t('fields.global.abbreviation')"
            />
          </v-flex>
          <v-flex md4 xs12 px-2>
            <v-text-field
              v-model="entity.web"
              :label="$t('titles.entities.siteUrl')"
              @change="onChangeWeb"
            />
          </v-flex>
          <v-flex md6 xs12 px-2>
            <v-combobox
              v-model="entity.telf"
              :label="$t('fields.users.phone')"
              chips
              multiple
              autocomplete="new-text"
              browser-autocomplete
            >
              <template #selection="data">
                <v-chip
                  color="primary"
                  dark
                  small
                  :selected="data.selected"
                  class="chip--select-multi"
                  close
                  @input="removeTelf(data.index)"
                >
                  {{ data.item }}
                </v-chip>
              </template>
            </v-combobox>
          </v-flex>
          <v-flex md6 xs12 px-2>
            <v-text-field
              v-model="entity.email"
              v-validate="'email'"
              :label="$t('fields.users.email')"
              :data-vv-name="$t('fields.users.email')"
              :error-messages="errors.collect($t('fields.users.email'))"
              autocomplete="new-text"
              browser-autocomplete
            />
          </v-flex>

          <v-flex md6 xs12 px-2>
            <v-text-field
              v-model="entity.address"
              :label="$t('fields.users.address')"
              autocomplete="new-text"
              browser-autocomplete
            />
          </v-flex>
          <v-flex md6 xs12 px-2>
            <v-text-field
              v-model="entity.cp"
              :label="$t('fields.users.cp')"
              autocomplete="new-text"
              browser-autocomplete
            />
          </v-flex>
          <v-flex md6 xs12 px-2>
            <v-text-field
              v-model="entity.fedCode"
              :label="$t('titles.entities.fedCode')"
            />
          </v-flex>
          <v-flex md6 xs12 px-2>
            <stadium-field
              v-model="entity.stadiumId"
              :label="
                $t('action.actions.select', { field: $tc('models.stadium', 2) })
              "
              multiple
            />
          </v-flex>

          <v-flex lg3 md6 xs12 px-2>
            <CountryField
              v-model="entity.countryId"
              clearable
              :label="
                $t('action.actions.select', { field: $tc('models.country') })
              "
              autocomplete="new-text"
              browser-autocomplete
            />
          </v-flex>
          <v-flex lg3 md6 xs12 px-2>
            <RegionField
              v-model="entity.regionId"
              :disabled="!entity.countryId"
              :country-id="entity.countryId"
              clearable
              :label="
                $t('action.actions.select', { field: $tc('models.region') })
              "
              autocomplete="new-text"
              browser-autocomplete
            />
          </v-flex>
          <v-flex lg3 md6 xs12 px-2>
            <ProvinceField
              v-model="entity.provinceId"
              :disabled="!entity.regionId"
              :region-id="entity.regionId"
              clearable
              :label="
                $t('action.actions.select', { field: $tc('models.province') })
              "
              autocomplete="new-text"
              browser-autocomplete
            />
          </v-flex>
          <v-flex lg3 md6 xs12 px-2>
            <TownField
              v-model="entity.townId"
              :disabled="!entity.provinceId"
              :province-id="entity.provinceId"
              :label="
                $t('action.actions.select', { field: $tc('models.town') })
              "
              autocomplete="new-text"
              browser-autocomplete
            />
          </v-flex>
          <v-flex md4 xs12 px-2>
            <v-text-field
              v-model="entity.facebook"
              v-validate="{ url: { require_protocol: true } }"
              prepend-inner-icon="fa fa-facebook"
              :label="$t('titles.entities.facebook')"
              :data-vv-name="$t('titles.entities.facebook')"
              :error-messages="errors.collect($t('titles.entities.facebook'))"
            />
          </v-flex>
          <v-flex md4 xs12 px-2>
            <v-text-field
              v-model="entity.twitter"
              prepend-inner-icon="fa fa-twitter"
              :label="$t('titles.entities.twitter')"
            />
          </v-flex>
          <v-flex md4 xs12 px-2>
            <v-text-field
              v-model="entity.instagram"
              prepend-inner-icon="fa fa-instagram"
              :label="$t('titles.entities.instagram')"
            />
          </v-flex>
          <v-flex xs12>
            <v-layout row wrap>
              <v-flex md3 sm6 xs12 px-2>
                <color-picker
                  v-model="entity.color"
                  v-validate="'excluded:#FFFFFF'"
                  :label="$t('fields.global.color')"
                  :data-vv-name="$t('fields.global.color')"
                  :error-messages="errors.collect($t('fields.global.color'))"
                />
              </v-flex>
              <v-flex md2 sm6 xs12 px-2>
                <v-switch
                  v-model="entity.active"
                  :label="$t('fields.global.active')"
                  color="primary"
                />
              </v-flex>
              <v-flex md2 sm6 xs12 px-2>
                <v-switch
                  v-model="entity.isCustomer"
                  :label="$t('titles.entities.client')"
                  color="primary"
                />
              </v-flex>
              <v-flex md2 sm6 xs12 px-2>
                <v-switch
                  v-model="entity.highSpeedInternet"
                  :label="$t('fields.global.highSpeedInternet')"
                  color="primary"
                />
              </v-flex>
              <v-flex md2 sm6 xs12 px-2>
                <v-switch
                  v-model="entity.systematicVideoRecovery"
                  :label="$t('fields.global.systematicVideoRecovery')"
                  color="primary"
                />
              </v-flex>

              <v-flex md2 sm6 xs12 px-2>
                <v-switch
                  v-model="entity.isDownloadable"
                  :label="$t('fields.global.downloadMatch')"
                  color="primary"
                />
              </v-flex>
              <v-flex v-if="isAdmin" md4 xs12 px-2>
                <v-text-field
                  v-model="entity.odooId"
                  :label="$t('titles.entities.dimaCode')"
                />
              </v-flex>
            </v-layout>
          </v-flex>
          <!-- Logo -->
          <v-flex xs12 pa-2>
            <button-upload
              :label="$t('fields.global.logo')"
              :image.sync="imageLogo"
              :title.sync="titleFileLogo"
              @file="(file) => (fileLogo = file)"
            />
          </v-flex>
          <!-- Home Img -->
          <v-flex xs12 pa-2>
            <button-upload
              :label="$t('fields.global.homeImg')"
              :image.sync="imageHomeImg"
              :title.sync="titleFileHomeImg"
              @file="(file) => (fileHomeImg = file)"
            />
          </v-flex>
        </v-layout>
      </v-card-text>
      <v-card-actions>
        <v-spacer />
        <meta-button :disabled="loading" type="reset" @click="clear" />
        <meta-button
          v-if="entity._id"
          :loading="loading"
          type="update"
          @click="submit"
        />
        <meta-button v-else :loading="loading" type="create" @click="submit" />
      </v-card-actions>
    </v-card>
  </v-container>
</template>

<script>
import { mapActions, mapGetters, mapState } from 'vuex'
import ColorPicker from '@/components/widgets/Input/ColorPicker'
import CountryField from '@/components/widgets/Input/CountryField'
import ProvinceField from '@/components/widgets/Input/ProvinceField'
import RegionField from '@/components/widgets/Input/RegionField'
import StadiumField from '@/components/widgets/Input/StadiumField'
import TownField from '@/components/widgets/Input/TownField'
import ButtonUpload from '@/components/widgets/button/ButtonUpload'
import { parseObject } from '@/helpers/utils'
export default {
  name: 'TabInformation',
  components: {
    ColorPicker,
    ButtonUpload,
    StadiumField,
    CountryField,
    RegionField,
    ProvinceField,
    TownField,
  },
  data() {
    return {
      entity: {},
      imageLogo: '',
      fileLogo: '',
      titleFileLogo: '',
      imageHomeImg: '',
      fileHomeImg: '',
      titleFileHomeImg: '',
    }
  },
  watch: {
    selected: {
      handler: function (val) {
        if (val) {
          this.setObject(val)
        }
      },
      immediate: true,
      deep: true,
    },
    'selected.logo': {
      handler: function (val) {
        if (val) {
          if (val.includes('http')) this.imageLogo = val
          else this.imageLogo = this.defaultValues.imageUrl + val
        } else {
          this.imageLogo = ''
        }
        this.titleFileLogo = val
        this.fileLogo = ''
      },
      immediate: true,
      deep: true,
    },
    'selected.homeImg': {
      handler: function (val) {
        if (val) {
          if (val.includes('http')) this.imageHomeImg = val
          else this.imageHomeImg = this.defaultValues.imageUrl + val
        } else {
          this.imageHomeImg = ''
        }
        this.titleFileHomeImg = val
        this.fileHomeImg = ''
      },
      immediate: true,
      deep: true,
    },
  },
  mounted() {
    this.setObject(this.selected)
    // console.log(this.selected)
  },
  computed: {
    ...mapState({
      selected: (state) => state.entities.selected,
      loading: (state) => state.entities.loading,
      listLanguages: (state) => state.languages.listSelectLanguage,
    }),
    ...mapGetters({
      stadiumSelectList: 'stadiums/selectList',
    }),
  },
  methods: {
    ...mapActions({
      setCreate: 'entities/create',
      setUpdate: 'entities/update',
      reset: 'entities/reset',
      fetchPitches: 'pitches/fetchList',
      uploadFile: 'files/uploadPush',
    }),
    submit() {
      this.validate().then((valid) => {
        if (!valid) return
        let payload = Object.assign({}, this.entity)

        if (!payload._id) {
          this.setCreate(payload).then((item) => {
            this.uploadFiles(item._id)

            this.$emit('close')
            this.clear()
          })
        } else {
          this.setUpdate(payload).then((item) => {
            this.uploadFiles(item._id)
            this.$emit('close')
            this.clear()
          })
        }
      })
    },
    uploadFiles(objectId) {
      if (this.fileLogo) {
        this.uploadFile({
          id: objectId,
          model: 'Entities',
          field: 'logo',
          file: this.fileLogo,
        })
      }

      if (this.fileHomeImg) {
        this.uploadFile({
          id: objectId,
          model: 'Entities',
          field: 'homeImg',
          file: this.fileHomeImg,
        })
      }
    },
    clear() {
      this.reset()
      this.$validator.reset()
    },
    removeStadium(item) {
      const index = this.entity.stadiumId.indexOf(item.value)
      if (index >= 0) this.entity.stadiumId.splice(index, 1)
    },
    removeTelf(index) {
      this.entity.telf.splice(index, 1)
    },
    onChangeWeb(web) {
      if (web && !web.includes('http')) {
        this.entity.web = 'https://' + web
      }
    },
    setObject(entity) {
      this.$nextTick(() => {
        this.entity = parseObject(entity)
        delete entity.smtp
      })
    },
  },
}
</script>
